import { StyleSheet, View, Text, Pressable, TextInput } from "react-native";
import WebPage from "@/ui/WebPage";
import { useTranslation } from "react-i18next";
import { ms } from "@/ui/moderateScale";
import { useUISlice } from "@/models/useUISlice";
import { useEffect, useState } from "react";
import { Colors } from "@/constants/Colors";
import { useUserSlice } from "@/models/useUserSlice";
import { useRouter } from "@/_router";
import { useRealtimeTranscription } from "@/models/useRealtimeTranscription";
import Menu from "@/ui/Menu";
import ProfilePicture from "@/ui/ProfilePicture";
import { useAuth } from "@/models/useAuth";
import ButtonDictationMobile from "@/ui/ButtonDictationMobile";
import { AIEditOption } from "@/models/types";
import SkeletonText from "@/ui/SkeletonText";
import { useClipboard } from "@/models/useClipboard";

export default function HomePage() {
  const { t } = useTranslation();
  const isMobile = useUISlice((state) => state.device.isMobile);
  const [isListening, setIsListening] = useState<boolean>(false);
  const height = useUISlice.getState().device.height;
  const router = useRouter();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTabOptions, setSelectedTabOptions] = useState<AIEditOption[]>(
    []
  );
  const user = useUserSlice((state) => state.userData);
  const { copy } = useClipboard();
  const [aiEdits, setAiEdits] = useState<{ [aiEditType: string]: string }>({});
  const [textState, setTextState] = useState({
    text: "",
    baseText: "",
    cursorPosition: 0,
  });
  const isAuthenticated = useUserSlice((state) => state.userToken);

  const { startListening, stopListening, finalText, pendingText } =
    useRealtimeTranscription({
      onConnectionOpened: () => {
        useUISlice.getState().resetUseRealtimeTranscription();
        setIsListening(true);
      },
      onConnectionClosed: () => {
        useUISlice.getState().resetUseRealtimeTranscription();
        setIsListening(false);
      },
    });

  useEffect(() => {
    if (!isAuthenticated) {
      useUISlice.getState().setAlertOnboardingVisible(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    useUserSlice.getState().user().profile({});

    if (isMobile) {
      // Apply the overlay color to the theme-color meta tag for web
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#ffffff");
      }

      return () => {
        if (metaThemeColor) {
          metaThemeColor.setAttribute("content", "");
        }
      };
    }
  }, []);

  useEffect(() => {
    // Batch all updates together when finalText or pendingText changes
    setSelectedTabOptions([]);
    setAiEdits({});
    setTextState((prev) => {
      let newBaseText = prev.baseText;
      let newCursorPosition = prev.cursorPosition;

      // Handle finalText updates
      if (finalText) {
        const beforeCursor = newBaseText.slice(0, prev.cursorPosition);
        const afterCursor = newBaseText.slice(prev.cursorPosition);

        const needsSpaceBefore =
          beforeCursor.length > 0 &&
          !beforeCursor.endsWith(" ") &&
          !finalText.startsWith(" ");

        const needsSpaceAfter =
          afterCursor.length > 0 &&
          !afterCursor.startsWith(" ") &&
          !finalText.endsWith(" ");

        const textToInsert =
          (needsSpaceBefore ? " " : "") +
          finalText +
          (needsSpaceAfter ? " " : "");

        newBaseText = beforeCursor + textToInsert + afterCursor;
        newCursorPosition = prev.cursorPosition + textToInsert.length;
      }

      // Calculate display text with pending text
      let displayText = newBaseText;
      if (pendingText) {
        const beforeCursor = displayText.slice(0, newCursorPosition);
        const afterCursor = displayText.slice(newCursorPosition);

        const needsSpaceBefore =
          beforeCursor.length > 0 &&
          !beforeCursor.endsWith(" ") &&
          !pendingText.startsWith(" ");

        const textToInsert = (needsSpaceBefore ? " " : "") + pendingText;
        displayText = beforeCursor + textToInsert + afterCursor;
      }

      return {
        text: displayText,
        baseText: newBaseText,
        cursorPosition: newCursorPosition,
      };
    });
  }, [finalText, pendingText]);

  useEffect(() => {
    const applyEdit = async () => {
      if (isLoading || textState?.text?.length === 0) return;
      if (selectedTabOptions.length === 0) return;

      const editId = _getEditId();
      if (aiEdits[editId]) return;

      setIsLoading(true);
      const outputText = await useUserSlice.getState().note().editWithAI({
        inputText: textState.text,
        editOptions: selectedTabOptions,
      });

      if (outputText) {
        setAiEdits({
          ...aiEdits,
          [editId]: outputText,
        });
      }

      setIsLoading(false);
    };

    applyEdit();
  }, [selectedTabOptions]);

  const _getEditId = () => {
    return `${selectedTabOptions.join("-")}`;
  };

  const _onMicPress = async ({ language }: { language: string }) => {
    const isSubscribed = useUserSlice.getState().isSubscribed;

    if (!isSubscribed) {
      useUISlice.getState().setPaywallVisible(true);
      return;
    }

    if (isListening) {
      await stopListening();

      // Clear transcription state after stopping
      useUISlice.getState().resetUseRealtimeTranscription();
      return;
    }

    await startListening(language);
  };

  const _onTabOptionPress = (option: AIEditOption) => {
    if (isLoading) return;

    const nextSelectedTabOptions = Array.from(new Set([...selectedTabOptions]));

    if (nextSelectedTabOptions.includes(option)) {
      setSelectedTabOptions(nextSelectedTabOptions.filter((o) => o !== option));
      return;
    }

    switch (option) {
      case "type_email":
        setSelectedTabOptions([
          ...nextSelectedTabOptions.filter((o) => !["type_text"].includes(o)),
          "type_email",
        ]);
        break;

      case "type_text":
        setSelectedTabOptions([
          ...nextSelectedTabOptions.filter((o) => !["type_email"].includes(o)),
          "type_text",
        ]);
        break;

      case "length_shorten":
        setSelectedTabOptions([
          ...nextSelectedTabOptions.filter(
            (o) => !["length_elaborate"].includes(o)
          ),
          "length_shorten",
        ]);
        break;

      case "length_elaborate":
        setSelectedTabOptions([
          ...nextSelectedTabOptions.filter(
            (o) => !["length_shorten"].includes(o)
          ),
          "length_elaborate",
        ]);
        break;

      case "tone_more_formal":
        setSelectedTabOptions([
          ...nextSelectedTabOptions.filter(
            (o) => !["tone_more_casual"].includes(o)
          ),
          "tone_more_formal",
        ]);
        break;

      case "tone_more_casual":
        setSelectedTabOptions([
          ...nextSelectedTabOptions.filter(
            (o) => !["tone_more_formal"].includes(o)
          ),
          "tone_more_casual",
        ]);
        break;
    }
  };

  const _getCurrentText = () => {
    if (selectedTabOptions.length === 0) {
      return textState.text;
    }

    const editId = _getEditId();

    if (aiEdits[editId]) {
      return aiEdits[editId];
    }
  };

  const _onCopyPress = () => {
    copy(_getCurrentText());
  };

  const _onSharePress = async () => {
    try {
      await navigator.share({ text: _getCurrentText() });
    } catch (error) {
      console.log(error);
    }
  };

  let textInputValue = _getCurrentText();

  return (
    <>
      <WebPage
        HeaderComponent={
          <View
            style={{
              backgroundColor: Colors.white,
              width: "100%",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              paddingHorizontal: ms(16),
              zIndex: 100,
              height: HEADER_HEIGHT,
              shadowColor: Colors.text,
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.1,
              shadowRadius: 2,
              ...(isMobile && {
                borderBottomWidth: StyleSheet.hairlineWidth,
                borderBottomColor: Colors.borderColor,
              }),
              ...{ position: "fixed" as any },
            }}
          >
            <View style={styles.headerLeft}>
              <Text
                style={{
                  fontStyle: "italic",
                  fontSize: ms(18),
                  fontWeight: "900",
                  letterSpacing: 0.8,
                  color: Colors.text,
                }}
              >
                ⚡️ EZWORDING
              </Text>
            </View>

            <View
              style={{
                ...styles.headerRight,
                ...(isMobile && { gap: ms(16) }),
              }}
            >
              {!isSubscribed && (
                <Pressable
                  onPress={() => useUISlice.getState().setPaywallVisible(true)}
                  style={{
                    paddingHorizontal: ms(16),
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: Colors.text,
                    height: ms(32),
                    borderRadius: ms(24),
                  }}
                >
                  <Text
                    style={{
                      fontSize: ms(12),
                      color: Colors.white,
                      fontWeight: "600",
                    }}
                  >
                    {t("screens.homePage.upgrade")}
                  </Text>
                </Pressable>
              )}

              <Menu
                key={`${user?.email}-${isSubscribed}`}
                actions={[
                  {
                    text: isSubscribed
                      ? t("screens.homePage.manageSubscription")
                      : t("screens.homePage.upgradeNow"),
                    onPress: () =>
                      isSubscribed
                        ? useUserSlice
                            .getState()
                            .subscription()
                            .stripeBillingPortal()
                        : useUISlice.getState().setPaywallVisible(true),
                  },
                  {
                    text: t("screens.homePage.logout"),
                    destructive: true,
                    onPress: () => useAuth.signOut(() => router.push("/")),
                  },
                ]}
                actionComponent={<ProfilePicture email={user?.email} />}
              />
            </View>
          </View>
        }
      >
        <View
          style={{
            flex: 1,
            maxWidth: EDITOR_WIDTH,
            alignSelf: "center",
            borderColor: Colors.borderColor,
            marginTop: HEADER_HEIGHT,
            width: "100%",
          }}
        >
          {/* if loading add Skeleton Text is a component for rendering placeholder content here */}
          {isLoading ? (
            <SkeletonText />
          ) : (
            <TextInput
              autoCorrect={false}
              spellCheck={false}
              value={textInputValue}
              onChangeText={(newText) => {
                setTextState({
                  text: newText,
                  baseText: newText,
                  cursorPosition: textState.cursorPosition,
                });
                setSelectedTabOptions([]);
                setAiEdits({});
              }}
              onSelectionChange={(event) => {
                // Only update cursor position, not the whole state
                setTextState((prev) => ({
                  ...prev,
                  cursorPosition: event.nativeEvent.selection.start,
                }));
              }}
              multiline
              placeholderTextColor={Colors.gray6}
              style={{
                width: "100%",
                padding: ms(16),
                fontSize: ms(18),
                height:
                  height -
                  HEADER_HEIGHT -
                  ButtonDictationMobile.HEIGHT -
                  ms(24),
                color: Colors.text,
                fontWeight: "400",
                lineHeight: ms(28),
                ...{ outline: "none" },
              }}
              placeholder={t("screens.homePage.editorPlaceholder")}
            />
          )}
        </View>
      </WebPage>

      <ButtonDictationMobile
        selectedTabOptions={selectedTabOptions}
        editOptions={
          textState.text.length > 0
            ? [
                {
                  label: t("screens.homePage.writeEmail"),
                  value: "type_email",
                  onPress: () => {
                    _onTabOptionPress("type_email");
                  },
                },
                // {
                //   label: t("screens.homePage.writeMessage"),
                //   value: "type_text",
                //   onPress: () => {
                //     _onTabOptionPress("type_text");
                //   },
                // },
                {
                  label: t("screens.homePage.shorten"),
                  value: "length_shorten",
                  onPress: () => {
                    _onTabOptionPress("length_shorten");
                  },
                },
                {
                  label: t("screens.homePage.elaborate"),
                  value: "length_elaborate",
                  onPress: () => {
                    _onTabOptionPress("length_elaborate");
                  },
                },
                {
                  label: t("screens.homePage.moreCasual"),
                  value: "tone_more_casual",
                  onPress: () => {
                    _onTabOptionPress("tone_more_casual");
                  },
                },
                {
                  label: t("screens.homePage.moreFormal"),
                  value: "tone_more_formal",
                  onPress: () => {
                    _onTabOptionPress("tone_more_formal");
                  },
                },
                // {
                //   label: "Retry",
                //   value: "retry",
                //   onPress: () => {
                //     _onTabOptionPress("retry");
                //   },
                // },
              ]
            : []
        }
        onMicPress={_onMicPress}
        isListening={isListening}
        onCopyPress={_onCopyPress}
        onSharePress={_onSharePress}
      />
    </>
  );
}

export const EDITOR_WIDTH = 720;
const HEADER_HEIGHT = ms(56);

const styles = StyleSheet.create({
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(20),
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(20),
  },
  headerText: {
    fontSize: ms(14),
    color: Colors.text,
  },
  headerIcon: {
    width: ms(20),
    height: ms(20),
    tintColor: Colors.text,
  },
  scrollView: {
    marginTop: ms(64),
    width: "100%",
    alignSelf: "center",
    flex: 1,
  },
  aiButtonContainer: {
    height: ms(32),
    borderRadius: ms(32),
    paddingHorizontal: ms(16),
    backgroundColor: Colors.accent + "08",
    borderColor: Colors.accent + "15",
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    justifyContent: "center",
  },
  aiButtonText: {
    fontSize: ms(14),
    color: Colors.accent,
    fontWeight: "400",
  },
  iconButton: {
    padding: ms(8),
    borderRadius: ms(4),
    backgroundColor: Colors.gray2, // Changed to an existing color
  },
  iconButtonText: {
    fontSize: ms(12),
    color: Colors.text, // Ensuring good contrast with gray2
  },
});
